import type { RespuestaLocal } from './respuestaLocal';
import { Check } from './iconos';
import { ImagenCuestionario } from './ImagenCuestionario';
import type { Escala, Pregunta, TipoPregunta } from '@copathy/shared';
import { OBSERVACION_ROTULO_POR_OMISION, etiquetaCabeEnPastilla } from '@copathy/shared';

/**
 * Una pregunta **tal como la verá quien la responda**, en un teléfono.
 *
 * ── Dónde se responde de verdad ──
 * En la app móvil (D-011), o en la web responsive en una pantalla angosta. De ahí
 * salen las dos reglas duras de este archivo:
 *
 * 1. **Cero scroll horizontal.** La matriz NO es una tabla: es cada afirmación
 *    con su escala debajo, apilada. Una tabla de siete columnas en 375 px o
 *    scrollea de lado o se vuelve ilegible — las dos cosas que un formulario
 *    cómodo no puede hacer. Jonathan lo señaló mirando la previa.
 * 2. **Área de toque generosa.** Cada opción es una pastilla de alto completo,
 *    no un circulito de 16 px al que hay que atinarle con el pulgar.
 *
 * ── El acento de sección ──
 * `acento` llega de la sección a la que pertenece la pregunta y tiñe el número,
 * los bordes activos y los controles. Es lo que rompe la monotonía de un
 * formulario largo sin convertirlo en un arcoíris: **una** familia de color por
 * página.
 *
 * ── Qué se reutiliza y qué no ──
 * ⚠️ Este archivo **no cruza a React Native** (CLAUDE.md): lo que cruza es la
 * forma, en `shared/`. Dentro de la web sí es la misma pieza que usará la
 * pantalla de responder (M7): hoy inerte, y cuando llegue el momento entran
 * `valor` y `alResponder` sin cambiarle la forma.
 *
 * ⚠️ **`inert` y no solo `pointer-events-none`**: un formulario invisible al
 * ratón sigue en el orden de tabulación.
 */

export type Acento = {
  /** Color pleno: números, bordes seleccionados. */
  fuerte: string;
  /** Fondo suave: chips, resaltados. */
  suave: string;
  /**
   * El color del TEXTO que va sobre `fuerte`.
   *
   * ⚠️ **Calculado, no elegido.** Desde que el color puede ser un hex libre
   * (036), fijar el texto en blanco deja ilegible cualquier acento claro — y la
   * regla de marca ya decía que sobre el naranja va marino y no blanco, porque
   * blanco da 2,84:1 (D-025). Se decide por luminancia: claro → texto oscuro.
   */
  textoSobreFuerte: string;
};

/**
 * La paleta, del nombre guardado al tono de la web. El nombre viaja por la API;
 * el tono lo decide cada pantalla — la app móvil tendrá el suyo.
 */
const PALETA: Record<string, { fuerte: string; suave: string }> = {
  marino: { fuerte: '#1c4270', suave: '#eef3f9' },
  naranja: { fuerte: '#e25703', suave: '#fff3ea' },
  verde: { fuerte: '#0e7a4e', suave: '#ecf8f1' },
  morado: { fuerte: '#6d28d9', suave: '#f4effd' },
  turquesa: { fuerte: '#0d7490', suave: '#ebf7fa' },
  rosa: { fuerte: '#be1862', suave: '#fdeef5' },
};

/** `#rrggbb` → sus tres componentes. Devuelve null si no es un hex de seis. */
function aRgb(hex: string): [number, number, number] | null {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

/**
 * La luminancia relativa de la WCAG. **Esta fórmula no se simplifica.**
 *
 * El atajo obvio —promediar R, G y B— se equivoca donde más importa: un amarillo
 * puro y un azul puro tienen promedios parecidos y luminancias opuestas, así que
 * el amarillo terminaría con texto blanco encima. Los coeficientes y la corrección
 * de gamma son lo que hace que el resultado coincida con lo que el ojo ve.
 */
function luminancia([r, g, b]: [number, number, number]): number {
  const canal = (c: number) => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
}

/**
 * De un color guardado —nombre de la paleta o hex— al acento completo.
 *
 * ⚠️ El **`suave`** de un hex libre se calcula mezclándolo con blanco al 92 %.
 * No se puede tomar de la paleta porque no hay entrada para un color arbitrario,
 * y usar el mismo tono con transparencia se ve distinto según lo que haya detrás
 * —una cabecera dentro de una tarjeta blanca no es lo mismo que sobre el lienzo—.
 */
export function acentoDeColor(color: string | null | undefined): Acento {
  const dePaleta = color ? PALETA[color] : undefined;
  const base = dePaleta ?? (color ? { fuerte: color, suave: '' } : undefined);
  if (!base) return ACENTO_BASE;

  const rgb = aRgb(base.fuerte);
  if (!rgb) return ACENTO_BASE;

  const suave = base.suave || `rgb(${rgb.map((c) => Math.round(c * 0.08 + 255 * 0.92)).join(' ')})`;

  /*
    El umbral en 0,45 y no en 0,5: el marino de la marca queda cómodamente del
    lado oscuro, y el naranja —el caso que la regla nombra— cae del lado claro y
    recibe texto marino, que es exactamente lo que dice D-025.
  */
  const textoSobreFuerte = luminancia(rgb) > 0.45 ? '#12233b' : '#ffffff';

  return { fuerte: base.fuerte, suave, textoSobreFuerte };
}

export const ACENTO_BASE: Acento = {
  fuerte: '#1c4270',
  suave: '#eef3f9',
  textoSobreFuerte: '#ffffff',
};

/**
 * La paleta como acentos completos. Se mantiene exportada porque el constructor
 * la usa para pintar las muestras del selector.
 */
export const ACENTOS: Record<string, Acento> = Object.fromEntries(
  Object.keys(PALETA).map((k) => [k, acentoDeColor(k)]),
);

export function PreguntaRespondible({
  pregunta,
  escala,
  numero,
  acento = ACENTO_BASE,
  resaltada,
  respuesta,
  alResponder,
}: {
  pregunta: Pregunta;
  escala: Escala | undefined;
  numero: number;
  acento?: Acento;
  /** La que se está editando ahora mismo en el constructor. */
  resaltada?: boolean;
  /**
   * La respuesta local (CC-12). **Controlado desde arriba y no con estado
   * propio**: la lista plegada de al lado tiene que mostrar lo mismo, y dos
   * copias del valor se desincronizan en cuanto una se remonta.
   */
  respuesta: RespuestaLocal | undefined;
  alResponder: (r: RespuestaLocal) => void;
}) {
  return (
    /*
      ⚠️ **Sin `inert`.** Lo tenía desde que esta previa era una maqueta, y cuando
      la escala pasó a ser tocable (D-131) el atributo se quedó: `inert` anula el
      puntero y el foco de TODOS los descendientes, así que los botones existían
      y respondían a un `click()` de script pero **una persona no podía tocarlos**.
      La comprobación de entonces usó clics programáticos y no lo vio.

      Desde CC-12 la previa se responde de verdad —es la única forma de ver la
      lista plegada con las respuestas dadas— así que `inert` no puede volver.
    */
    <article
      className="rounded-2xl border bg-white p-4 shadow-[0_1px_2px_rgba(15,36,64,0.06)] transition"
      style={{
        borderColor: resaltada ? acento.fuerte : '#e4ebf3',
        boxShadow: resaltada ? `0 0 0 4px ${acento.suave}` : undefined,
      }}
    >
      <div className="flex items-start gap-3">
        <span
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-sm font-bold"
          style={{ background: acento.fuerte, color: acento.textoSobreFuerte }}
        >
          {numero}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold leading-snug text-marino-900">
            {pregunta.emoji && (
              <span className="mr-1.5" aria-hidden="true">
                {pregunta.emoji}
              </span>
            )}
            {pregunta.texto || <span className="font-normal text-marino-300">Sin enunciado todavía</span>}
            {pregunta.obligatoria && (
              <span className="ml-1" style={{ color: acento.fuerte }}>
                *
              </span>
            )}
          </p>
          {pregunta.ayuda && <p className="mt-1 text-sm text-marino-400">{pregunta.ayuda}</p>}
        </div>
      </div>

      <div className="mt-3.5">
        <Control
          pregunta={pregunta}
          escala={escala}
          acento={acento}
          respuesta={respuesta}
          alResponder={alResponder}
        />
        {/*
          El campo abierto va acá, FUERA del `Control` y después de él: es lo que
          aparece debajo de las opciones, no otra forma de responder. Metido
          dentro del `switch` por tipo habría que repetirlo en cuatro ramas.
        */}
        {pregunta.admiteObservacion && (
          <CampoObservacion
            rotulo={pregunta.observacionRotulo}
            valor={respuesta?.observacion ?? ''}
            alCambiar={(observacion) => alResponder({ ...respuesta, observacion })}
          />
        )}
      </div>
    </article>
  );
}

function Control({
  pregunta,
  escala,
  acento,
  respuesta,
  alResponder,
}: {
  pregunta: Pregunta;
  escala: Escala | undefined;
  acento: Acento;
  respuesta: RespuestaLocal | undefined;
  alResponder: (r: RespuestaLocal) => void;
}) {
  const tipo: TipoPregunta = pregunta.tipo;
  /** Cambiar un campo conservando el resto —la observación, sobre todo—. */
  const poner = (cambio: Partial<RespuestaLocal>) => alResponder({ ...respuesta, ...cambio });

  if (tipo === 'likert') {
    if (!escala) return <Pendiente>Elige una escala para ver cómo queda.</Pendiente>;
    return (
      <FilaEscala
        escala={escala}
        acento={acento}
        elegido={respuesta?.punto}
        alElegir={(punto) => poner({ punto })}
      />
    );
  }

  if (tipo === 'matriz_likert') {
    if (!escala) return <Pendiente>Elige una escala para ver cómo queda.</Pendiente>;
    if (pregunta.items.length === 0) {
      return <Pendiente>Agrega las afirmaciones que van en la matriz.</Pendiente>;
    }
    /*
      ⚠️ NADA de tabla. En un teléfono, la matriz es cada afirmación con su
      escala debajo, apilada. La misma escala se repite —eso es lo que la hace
      matriz—, pero el pulgar nunca tiene que salir del ancho de la pantalla.
    */
    return (
      <div className="space-y-3.5">
        {pregunta.items.map((item, i) => (
          <div
            key={item.id}
            className={i > 0 ? 'border-t border-dashed border-marino-100 pt-3.5' : ''}
          >
            <p className="mb-2 text-sm font-medium text-marino-700">{item.texto}</p>
            <FilaEscala
              escala={escala}
              acento={acento}
              compacta
              elegido={respuesta?.matriz?.[item.id]}
              alElegir={(punto) =>
                poner({ matriz: { ...(respuesta?.matriz ?? {}), [item.id]: punto } })
              }
            />
          </div>
        ))}
      </div>
    );
  }

  if (tipo === 'opcion_unica' || tipo === 'opcion_multiple' || tipo === 'si_no') {
    /*
      `si_no` no guarda ítems: sus dos opciones son fijas. Teclear «Sí» y «No» en
      cada pregunta es fricción, y guardarlos permitiría un sí/no cuyas opciones
      dijeran otra cosa.
    */
    const opciones =
      tipo === 'si_no'
        ? [{ id: -1, texto: 'Sí' }, { id: -2, texto: 'No' }]
        : pregunta.items.map((i) => ({ id: i.id, texto: i.texto }));

    if (opciones.length === 0) return <Pendiente>Agrega al menos dos opciones.</Pendiente>;

    const multiple = tipo === 'opcion_multiple';
    const marcadas = respuesta?.opciones ?? [];
    const marcada = (id: number) => (multiple ? marcadas.includes(id) : respuesta?.opcion === id);

    const alternar = (id: number) => {
      if (tipo === 'si_no') {
        poner({ bool: id === -1 });
        return;
      }
      if (!multiple) {
        /*
          Volver a tocar la opción elegida la DESMARCA. En una selección única sin
          esto, quien se equivoca no tiene forma de dejar la pregunta en blanco:
          la única salida sería elegir otra cosa, o sea responder algo falso.
        */
        poner({ opcion: respuesta?.opcion === id ? undefined : id });
        return;
      }
      poner({
        opciones: marcadas.includes(id) ? marcadas.filter((x) => x !== id) : [...marcadas, id],
      });
    };

    return (
      <>
        {/*
          `<ul>` con botones y no `<input type=radio>`: la fila entera es el
          objetivo del pulgar, y un radio nativo obliga a envolver todo en un
          `<label>` que después pelea con el estilo. `aria-checked` y el rol dejan
          el significado intacto para un lector de pantalla.
        */}
        <ul className="space-y-2" role={multiple ? 'group' : 'radiogroup'}>
          {opciones.map((o) => {
            const activa = marcada(o.id);
            return (
              <li key={o.id}>
                <button
                  type="button"
                  role={multiple ? 'checkbox' : 'radio'}
                  aria-checked={activa}
                  onClick={() => alternar(o.id)}
                  className="flex w-full items-center gap-3 rounded-xl border bg-white px-3.5 py-2.5
                    text-left text-sm text-marino-800 transition
                    focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
                  style={{
                    borderColor: activa ? acento.fuerte : '#e4ebf3',
                    background: activa ? acento.suave : '#fff',
                  }}
                >
                  <span
                    className={`grid h-[18px] w-[18px] shrink-0 place-items-center border-2 ${
                      multiple ? 'rounded-[5px]' : 'rounded-full'
                    }`}
                    style={{
                      borderColor: acento.fuerte,
                      background: activa ? acento.fuerte : '#fff',
                      color: acento.textoSobreFuerte,
                    }}
                  >
                    {activa && <Check size={11} />}
                  </span>
                  {o.texto}
                </button>
              </li>
            );
          })}
        </ul>
        {multiple && (pregunta.minimo !== null || pregunta.maximo !== null) && (
          <p className="mt-2 text-xs text-marino-400">
            {pregunta.minimo !== null && pregunta.maximo !== null
              ? `Elige entre ${pregunta.minimo} y ${pregunta.maximo}.`
              : pregunta.maximo !== null
                ? `Elige hasta ${pregunta.maximo}.`
                : `Elige al menos ${pregunta.minimo}.`}
          </p>
        )}
      </>
    );
  }

  if (tipo === 'texto_corto') {
    return (
      <input
        value={respuesta?.texto ?? ''}
        onChange={(e) => poner({ texto: e.target.value })}
        placeholder="Escribe tu respuesta…"
        className="w-full rounded-xl border border-marino-200 bg-white px-3.5 py-2.5 text-sm
          text-marino-800 placeholder:text-marino-300
          focus:outline-none focus:ring-4 focus:ring-marino-700/10"
      />
    );
  }

  if (tipo === 'texto_largo') {
    return (
      <textarea
        value={respuesta?.texto ?? ''}
        onChange={(e) => poner({ texto: e.target.value })}
        rows={3}
        placeholder="Escribe tu respuesta…"
        className="w-full resize-none rounded-xl border border-marino-200 bg-white px-3.5 py-2.5
          text-sm text-marino-800 placeholder:text-marino-300
          focus:outline-none focus:ring-4 focus:ring-marino-700/10"
      />
    );
  }

  /*
    ── La barra (029) ──
    Se dibuja **interactiva**, no como una caja de muestra: la previa existe para
    probar el recorrido, y en una barra el recorrido ES la pregunta. Una imagen
    fija no dejaría ver si los extremos se entienden ni si el rango tiene sentido,
    que es justo lo que hay que revisar antes de publicar.

    ⚠️ El `input[type=range]` nativo se usa a propósito: trae el arrastre con el
    dedo, el teclado —flechas, Inicio, Fin— y el anuncio del valor en un lector
    de pantalla, gratis y bien hechos. Un deslizador propio con `div` y eventos
    de puntero pierde las tres cosas y hay que reconstruirlas a mano.
  */
  if (tipo === 'barra') {
    const min = pregunta.minimo ?? 0;
    const max = pregunta.maximo ?? 100;
    return (
      <div>
        <input
          type="range"
          min={min}
          max={max}
          value={respuesta?.punto ?? Math.round((min + max) / 2)}
          onChange={(e) => poner({ punto: Number(e.target.value) })}
          aria-label={pregunta.texto}
          className="w-full accent-[var(--color-naranja-500)]"
        />
        {/*
          El valor a la vista. Sin él, una barra de 0 a 100 obliga a adivinar
          dónde quedó el dedo — y en el resumen de la lista plegada va a aparecer
          un número, así que quien responde tiene que poder verlo antes.
        */}
        <p className="mt-1 text-center text-sm font-bold" style={{ color: acento.fuerte }}>
          {respuesta?.punto ?? Math.round((min + max) / 2)}
        </p>
        {/*
          Los rótulos debajo de sus extremos. Si no los pusieron, se muestran los
          números: es peor que un rótulo, pero mucho mejor que una barra muda.
        */}
        <div className="mt-1 flex justify-between text-xs text-marino-500">
          <span>{pregunta.etiquetaMin ?? String(min)}</span>
          <span>{pregunta.etiquetaMax ?? String(max)}</span>
        </div>
      </div>
    );
  }

  if (tipo === 'numero') {
    const rango =
      pregunta.minimo !== null && pregunta.maximo !== null
        ? `Entre ${pregunta.minimo} y ${pregunta.maximo}`
        : pregunta.maximo !== null
          ? `Hasta ${pregunta.maximo}`
          : pregunta.minimo !== null
            ? `Desde ${pregunta.minimo}`
            : 'Un número';
    return (
      <input
        type="number"
        inputMode="numeric"
        value={respuesta?.numero ?? ''}
        onChange={(e) => poner({ numero: e.target.value })}
        min={pregunta.minimo ?? undefined}
        max={pregunta.maximo ?? undefined}
        placeholder={rango}
        aria-label={pregunta.texto}
        className="w-full max-w-40 rounded-xl border border-marino-200 bg-white px-3.5 py-2.5
          text-sm text-marino-800 placeholder:text-marino-300
          focus:outline-none focus:ring-4 focus:ring-marino-700/10"
      />
    );
  }

  if (tipo === 'fecha') {
    return (
      <input
        type="date"
        value={respuesta?.fecha ?? ''}
        onChange={(e) => poner({ fecha: e.target.value })}
        aria-label={pregunta.texto}
        className="w-full max-w-44 rounded-xl border border-marino-200 bg-white px-3.5 py-2.5
          text-sm text-marino-800
          focus:outline-none focus:ring-4 focus:ring-marino-700/10"
      />
    );
  }

  /*
    ── Los dos multimedia (034) ──
    Acá se DIBUJAN, no se usan: esta pantalla es la previa del constructor y no
    sube ni graba nada. Lo que importa que se vea es el espacio que ocupan y,
    sobre todo, **el aviso**.
  */
  if (tipo === 'archivo') {
    return (
      <div className="rounded-2xl border border-dashed border-marino-200 bg-white px-4 py-5 text-center">
        <p className="text-sm font-semibold text-marino-600">Adjuntar una foto o un archivo</p>
        <p className="mt-1 text-xs text-marino-400">Imagen o PDF, hasta 10 MB.</p>
        {/*
          ⚠️ En la previa **no se sube nada**: se simula para poder ver cómo queda
          la fila plegada con un adjunto puesto. Subir de verdad desde acá
          guardaría un archivo que no es respuesta de ninguna aplicación.
        */}
        <button
          type="button"
          onClick={() =>
            poner({ adjunto: respuesta?.adjunto ? undefined : { nombre: 'Foto adjunta' } })
          }
          className="mt-3 inline-block rounded-2xl bg-naranja-500 px-4 py-2 text-sm font-bold
            text-marino-900 transition hover:bg-naranja-400
            focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
        >
          {respuesta?.adjunto ? 'Quitar (simulado)' : 'Elegir archivo'}
        </button>
      </div>
    );
  }

  if (tipo === 'video') {
    return (
      <div className="rounded-2xl border border-dashed border-marino-200 bg-white px-4 py-5 text-center">
        <p className="text-sm font-semibold text-marino-600">Grabar o adjuntar un video</p>
        <p className="mx-auto mt-2 max-w-[16rem] text-xs leading-relaxed text-marino-500">
          Tu video lo ve <strong className="font-semibold">solo COPATHY</strong>. No llega a tu
          empresa.
        </p>
        <button
          type="button"
          onClick={() =>
            poner({ adjunto: respuesta?.adjunto ? undefined : { nombre: 'Video', segundos: 25 } })
          }
          className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-naranja-500 px-4 py-2
            text-sm font-bold text-marino-900 transition hover:bg-naranja-400
            focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
        >
          <span className="h-2.5 w-2.5 rounded-full bg-red-600" />
          {respuesta?.adjunto ? 'Borrar (simulado)' : 'Grabar video'}
        </button>
        <p className="mt-1.5 text-xs text-marino-400">Hasta 1 minuto.</p>
      </div>
    );
  }

  if (tipo === 'nota_voz') {
    return (
      <div className="rounded-2xl border border-dashed border-marino-200 bg-white px-4 py-5 text-center">
        <p className="text-sm font-semibold text-marino-600">Grabar una nota de voz</p>
        {/*
          ⚠️ **Este aviso no es un texto de relleno y no se quita.** La migración
          034 dice, por escrito, que lo que sostiene la decisión de producto es
          que al participante se le avise ANTES de grabar: su voz es un dato
          biométrico, no llega nunca a su empresa, y tiene derecho a saber las
          dos cosas antes de apretar el botón. Si esto desaparece, la 034 deja de
          significar lo que dice.
        */}
        <p className="mx-auto mt-2 max-w-[16rem] text-xs leading-relaxed text-marino-500">
          Tu grabación la escucha <strong className="font-semibold">solo COPATHY</strong>. No llega
          a tu empresa. Si prefieres no grabar, puedes escribirlo.
        </p>
        <button
          type="button"
          onClick={() =>
            poner({
              adjunto: respuesta?.adjunto ? undefined : { nombre: 'Grabación', segundos: 42 },
            })
          }
          className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-naranja-500 px-4 py-2
            text-sm font-bold text-marino-900 transition hover:bg-naranja-400
            focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
        >
          <span className="h-2.5 w-2.5 rounded-full bg-red-600" />
          {respuesta?.adjunto ? 'Borrar (simulado)' : 'Grabar'}
        </button>
        <p className="mt-1.5 text-xs text-marino-400">Hasta 10 minutos.</p>
      </div>
    );
  }

  // Las secciones no llegan acá: el renderizador de páginas las convierte en
  // cabeceras antes de repartir preguntas.
  return null;
}

/**
 * La escala como fila de pastillas o de caritas, y **el significado aparece al
 * elegir**, no antes.
 *
 * ── Por qué se fueron los rótulos de los extremos ──
 * Estaban los dos siempre puestos debajo, «Totalmente en desacuerdo» a la
 * izquierda y «Totalmente de acuerdo» a la derecha. En 350 px de ancho eso son
 * dos bloques de texto de tres líneas cada uno que empujan la pregunta hacia
 * abajo y **solo explican los dos extremos**: de los tres puntos del medio, el
 * trabajador sigue sin saber qué significan.
 *
 * Ahora la fila va limpia y al tocar una opción aparece debajo **qué significa
 * ESA**, la que se acaba de elegir. Se explica el punto que importa —el elegido—
 * en vez de dos que probablemente no sean.
 *
 * ⚠️ **El hueco del rótulo se reserva vacío.** Sin reservarlo, elegir una opción
 * empuja todo lo que hay debajo y en una matriz de seis afirmaciones la pantalla
 * salta con cada toque.
 *
 * ⚠️ Y esto obliga a algo del modelo: **cada punto necesita su etiqueta**, no
 * solo el primero y el último. Las escalas sembradas la tienen (D-064), pero una
 * escala nueva con los puntos del medio en blanco ahora se nota — antes esas
 * etiquetas no se mostraban nunca.
 */
function FilaEscala({
  escala,
  acento,
  compacta,
  elegido,
  alElegir,
}: {
  escala: Escala;
  acento: Acento;
  compacta?: boolean;
  /** El ORDEN del punto elegido, no su id. */
  elegido: number | undefined;
  alElegir: (orden: number | undefined) => void;
}) {
  /*
    ⚠️ **Controlada desde arriba, y por el ORDEN del punto y no por su id.**

    Con el id, una matriz de seis afirmaciones que comparten escala no podría
    guardar seis respuestas distintas —los puntos son los mismos objetos— y
    además el orden es lo que viaja al envío de verdad (`punto` en
    `ValorRespuesta`).

    Tenía estado propio, y eso rompía la lista plegada de CC-12: la fila decía
    «sin responder» mientras la escala de al lado estaba marcada.
  */
  const punto = escala.puntos.find((p) => p.orden === elegido);

  // La regla vive en `shared/` —`etiquetaCabeEnPastilla`— con su porqué y su
  // prueba: arregla que «Del 0 al 10» se dibujara numerada 1–11.

  return (
    <div>
      <div className="flex gap-1.5">
        {escala.puntos.map((p) => {
          const activo = p.orden === elegido;

          /*
            ── Tres formas de dibujar un punto, en este orden ──
            1. La **imagen del set**, si la escala usa uno (CC-04, 033). Manda
               sobre el emoji: quien eligió un set de caritas propias no quiere
               las del sistema.
            2. El **emoji** del punto.
            3. Una **pastilla** con su etiqueta si cabe, o su número (D-133).
          */
          if (p.imagenId !== null) {
            return (
              <button
                type="button"
                key={p.id}
                onClick={() => alElegir(activo ? undefined : p.orden)}
                aria-label={p.etiqueta}
                aria-pressed={activo}
                className={`grid flex-1 place-items-center transition
                  focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
                  ${compacta ? 'h-10' : 'h-12'}
                  ${activo ? 'scale-125' : elegido === null ? '' : 'scale-90 opacity-40'}`}
              >
                <ImagenCuestionario
                  ruta={`/api/cuestionarios/sets/imagenes/${p.imagenId}`}
                  hay
                  alt=""
                  className={compacta ? 'h-9 w-9' : 'h-11 w-11'}
                />
              </button>
            );
          }

          return p.emoji ? (
            /*
              La carita va SOLA, sin caja ni borde — Jonathan lo pidió así: el
              emoji ya es la figura, y encerrarlo en un botón lo vuelve un icono
              más. La elegida se marca con tamaño y opacidad, no con un marco.
            */
            <button
              type="button"
              key={p.id}
              onClick={() => alElegir(activo ? undefined : p.orden)}
              aria-label={p.etiqueta}
              aria-pressed={activo}
              className={`grid flex-1 place-items-center leading-none transition
                focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
                ${compacta ? 'h-10 text-2xl' : 'h-12 text-3xl'}
                ${activo ? 'scale-125' : elegido === null ? '' : 'scale-90 opacity-40'}`}
            >
              {p.emoji}
            </button>
          ) : (
            <button
              type="button"
              key={p.id}
              onClick={() => alElegir(activo ? undefined : p.orden)}
              aria-label={p.etiqueta}
              aria-pressed={activo}
              className={`grid flex-1 place-items-center rounded-xl border font-semibold transition
                focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
                ${compacta ? 'h-10 text-xs' : 'h-12 text-sm'}
                ${activo ? '' : 'text-marino-600'}`}
              style={
                activo
                  ? {
                      borderColor: acento.fuerte,
                      background: acento.fuerte,
                      color: acento.textoSobreFuerte,
                    }
                  : { borderColor: '#dbe5f0', background: acento.suave }
              }
            >
              {etiquetaCabeEnPastilla(p.etiqueta) ? p.etiqueta.trim() : p.orden}
            </button>
          );
        })}
      </div>

      {/*
        El hueco se reserva siempre —`min-h`— y solo se llena al elegir. Antes de
        elegir no se pone un texto de relleno: una línea de ayuda permanente
        vuelve a ser el ruido que se acaba de quitar.
      */}
      {/*
        El hueco se reserva siempre —`min-h`— y solo se llena al elegir. Antes de
        elegir no se pone un texto de relleno: una línea de ayuda permanente
        vuelve a ser el ruido que se acaba de quitar.

        Y **no repite lo que la pastilla ya dice**: en una escala 0–10 el número
        está a la vista, así que un «6» debajo del 6 no informa de nada.
      */}
      <p
        className="mt-2 min-h-[1.25rem] text-center text-xs font-semibold leading-tight"
        aria-live="polite"
        style={{ color: punto ? acento.fuerte : 'transparent' }}
      >
        {!punto ||
        (punto.imagenId === null && !punto.emoji && etiquetaCabeEnPastilla(punto.etiqueta))
          ? '\u00a0'
          : punto.etiqueta.trim() || 'Este punto no tiene rótulo — ponle uno'}
      </p>
    </div>
  );
}

/**
 * El campo abierto que va DEBAJO de las opciones (CC-11, 035).
 *
 * ⚠️ **Opcional siempre, aunque la pregunta sea obligatoria.** La
 * obligatoriedad es de la respuesta; exigir el comentario convertiría un
 * «cuéntanos si quieres» en un peaje, y lo que se recibiría son frases escritas
 * a la fuerza para poder seguir. Lo dice el rótulo, no solo el código.
 */
function CampoObservacion({
  rotulo,
  valor,
  alCambiar,
}: {
  rotulo: string | null;
  valor: string;
  alCambiar: (v: string) => void;
}) {
  return (
    <div className="mt-3">
      <p className="mb-1.5 text-xs font-medium text-marino-500">
        {rotulo?.trim() || OBSERVACION_ROTULO_POR_OMISION}{' '}
        <span className="text-marino-400">(opcional)</span>
      </p>
      <textarea
        value={valor}
        onChange={(e) => alCambiar(e.target.value)}
        rows={2}
        placeholder="Escribe tu comentario…"
        className="w-full resize-none rounded-xl border border-marino-200 bg-white px-3.5 py-2.5
          text-sm text-marino-800 placeholder:text-marino-300
          focus:outline-none focus:ring-4 focus:ring-marino-700/10"
      />
    </div>
  );
}

/** Lo que falta para que la pregunta esté completa. Se ve en la vista previa. */
function Pendiente({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-xl border border-dashed border-marino-200 bg-marino-50/40 px-3.5 py-3 text-sm text-marino-400">
      {children}
    </p>
  );
}

